import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import firebase from 'firebase/compat/app';
import { combineLatest, firstValueFrom, map, Observable } from 'rxjs';
import { IUserMcp, UserMcpTransport } from '@platfio/interfaces/platfio';
import { environment } from 'src/environments/environment';

export interface UserMcpUpsertInput {
  mcpId: string;
  name: string;
  note?: string | null;
  serverUrl: string;
  transport?: UserMcpTransport;
  headers?: Record<string, string> | null;
  enabled: boolean;
  agencyId?: string | null;
  agencyShared?: boolean;
}

export interface UserMcpTestInput {
  serverUrl: string;
  transport?: UserMcpTransport;
  headers?: Record<string, string> | null;
}

export interface UserMcpTestResult {
  ok: true;
  message: string;
  protocolVersion: string;
  serverName: string | null;
  serverVersion: string | null;
  toolCount: number;
  toolNames: string[];
}

@Injectable({
  providedIn: 'root'
})
export class UserMcpService {
  private readonly api = environment.useEmulator
    ? `http://127.0.0.1:5001/${environment.firebaseConfig.projectId}/us-central1/app`
    : `https://us-central1-${environment.firebaseConfig.projectId}.cloudfunctions.net/app`;

  constructor(private afs: AngularFirestore, private http: HttpClient) {}

  getMcps(uid: string): Observable<IUserMcp[]> {
    return this.afs.collection<IUserMcp>(`users/${uid}/mcps`, ref =>
      ref.orderBy('updatedAt', 'desc')
    ).valueChanges({ idField: 'mcpId' as never }) as Observable<IUserMcp[]>;
  }

  getAccessibleMcps(uid: string, agencyId?: string | null): Observable<IUserMcp[]> {
    const normalizedAgencyId = normalizeAgencyId(agencyId, uid);
    if (!normalizedAgencyId) {
      return this.getMcps(uid);
    }

    const ownMcps$ = this.getMcps(uid);
    const sharedMcps$ = this.afs.collectionGroup<IUserMcp>('mcps', ref =>
      ref
        .where('agencyId', '==', normalizedAgencyId)
        .where('agencyShared', '==', true)
    ).valueChanges({ idField: 'mcpId' as never }) as Observable<IUserMcp[]>;

    return combineLatest([ownMcps$, sharedMcps$]).pipe(
      map(([ownMcps, sharedMcps]) => mergeMcps(ownMcps || [], sharedMcps || []))
    );
  }

  async upsertMcp(uid: string, input: UserMcpUpsertInput): Promise<void> {
    const serverUrl = validateServerUrl(input.serverUrl);
    const headers = normalizeHeaders(input.headers);
    const ref = this.afs.doc(`users/${uid}/mcps/${input.mcpId}`);
    const existing = await ref.get().toPromise();
    const now = firebase.firestore.FieldValue.serverTimestamp();
    const agencyId = normalizeAgencyId(input.agencyId, uid);

    await ref.set({
      mcpId: input.mcpId,
      userId: uid,
      agencyId,
      agencyShared: input.agencyShared === true,
      name: input.name.trim(),
      note: input.note?.trim() || null,
      description: input.note?.trim() || null,
      serverUrl,
      transport: input.transport || 'streamable-http',
      headers,
      enabled: input.enabled,
      createdAt: existing?.exists ? existing.get('createdAt') || now : now,
      updatedAt: now
    }, { merge: true });
  }

  async deleteMcp(uid: string, mcpId: string): Promise<void> {
    await this.afs.doc(`users/${uid}/mcps/${mcpId}`).delete();
  }

  async testMcp(input: UserMcpTestInput): Promise<UserMcpTestResult> {
    const serverUrl = validateServerUrl(input.serverUrl);
    const headers = normalizeHeaders(input.headers);

    try {
      return await firstValueFrom(this.http.post<UserMcpTestResult>(`${this.api}/mcp/test`, {
        serverUrl,
        transport: input.transport || 'streamable-http',
        headers
      }));
    } catch (error: unknown) {
      throw new Error(this.getTestMcpErrorMessage(error));
    }
  }

  private getTestMcpErrorMessage(error: unknown): string {
    if (!(error instanceof HttpErrorResponse)) {
      return (error as Error)?.message || 'Unable to test this connector right now.';
    }

    if (error.status === 404) {
      return 'MCP connection testing is unavailable on this backend right now. Deploy the backend functions or use the emulator to test connectors.';
    }

    if (error.status === 0) {
      return 'Unable to reach the MCP test endpoint right now.';
    }

    const responseMessage = this.extractHttpErrorMessage(error.error);
    if (responseMessage) {
      return responseMessage;
    }

    return error.message || 'Unable to test this connector right now.';
  }

  private extractHttpErrorMessage(errorBody: unknown): string | null {
    if (!errorBody) {
      return null;
    }

    if (typeof errorBody === 'string') {
      const trimmed = errorBody.trim();
      if (!trimmed) {
        return null;
      }

      if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) {
        return null;
      }

      return trimmed;
    }

    if (typeof errorBody === 'object' && typeof (errorBody as { message?: unknown }).message === 'string') {
      return String((errorBody as { message?: unknown }).message).trim() || null;
    }

    return null;
  }
}

function validateServerUrl(value: string): string {
  const url = new URL(value.trim());
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    throw new Error('Connector URL must use http or https.');
  }

  return url.toString();
}

function normalizeHeaders(headers: Record<string, string> | null | undefined): Record<string, string> | null {
  if (!headers) {
    return null;
  }

  const normalized = Object.entries(headers).reduce<Record<string, string>>((accumulator, [key, value]) => {
    const normalizedKey = key.trim();
    const normalizedValue = String(value || '').trim();

    if (normalizedKey && normalizedValue) {
      accumulator[normalizedKey] = normalizedValue;
    }

    return accumulator;
  }, {});

  return Object.keys(normalized).length > 0 ? normalized : null;
}

function normalizeAgencyId(agencyId: string | null | undefined, fallbackUid: string): string {
  const normalizedAgencyId = String(agencyId || '').trim();
  return normalizedAgencyId || fallbackUid;
}

function mergeMcps(ownMcps: IUserMcp[], sharedMcps: IUserMcp[]): IUserMcp[] {
  const mcpById = new Map<string, IUserMcp>();

  for (const mcp of [...ownMcps, ...sharedMcps]) {
    if (!mcpById.has(mcp.mcpId)) {
      mcpById.set(mcp.mcpId, mcp);
    }
  }

  return Array.from(mcpById.values()).sort((left, right) => getDateValue(right.updatedAt) - getDateValue(left.updatedAt));
}

function getDateValue(value: unknown): number {
  if (!value) {
    return 0;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? 0 : value.getTime();
  }

  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    const date = (value as { toDate: () => Date }).toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
  }

  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}